/**
 * Carrying the server's own refusal from the provider button to the screen
 * that shows it.
 *
 * The three sign-in flows all end the same way — navigate to "/" with
 * `auth_error=1`, where `SignInButtons` prints one fixed generic sentence.
 * That is right for "Google said no", and wrong for the one refusal written
 * for the person to read: a blocked account is answered with a localized
 * sentence naming the reason and where to write, and replacing it with
 * "sign-in failed" costs them the only instruction they get.
 *
 * sessionStorage rather than router state or the query string. The X flow
 * ends in a loader `redirect()`, which carries no state; and a sentence in
 * the URL is a sentence anybody can put there, on a page that renders it.
 */
import { ERROR_ID } from "@/api/errorIds";
import { webStorage } from "@/lib/webStorage";

export const SIGN_IN_ERROR_KEY = "selfeed.web.sign_in_error";

type ErrorStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultStorage(): ErrorStorage | null {
  // Same reasoning as `lib/session`: on the SSR server "no storage" is the
  // truth, Node's global may exist without working methods, and a browser
  // that blocks site data throws from the getter.
  return webStorage("session");
}

/**
 * The id the backend tags a blocked-account refusal with. It is the only 4xx
 * from these endpoints whose title was written for the person to read and
 * translated by Accept-Language; the rest carry developer strings in one
 * language — "Invalid Google token", "code_verifier is required" — and
 * showing those would replace a correctly translated generic sentence with
 * English for a visitor who cannot read it.
 */
const SHOWABLE_ERROR_ID = ERROR_ID.account;

/** The backend answers every refusal in one shape, `{ errors: [{ id, title }] }`. */
export function authErrorTitle(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const { errors } = error as { errors?: unknown };
  if (!Array.isArray(errors) || errors.length === 0) return undefined;
  const first = errors[0] as { id?: unknown; title?: unknown } | undefined;
  if (first?.id !== SHOWABLE_ERROR_ID) return undefined;
  const title = typeof first?.title === "string" ? first.title.trim() : "";
  return title ? title : undefined;
}

/**
 * Park the message, or clear whatever was parked when this attempt produced
 * none. The clear is the half that is easy to leave out: without it an
 * ordinary failure after an unread block message leaves the block sentence
 * standing, and the next attempt shows it for an account that is not blocked.
 */
export function rememberSignInError(
  message: string | undefined,
  storage: ErrorStorage | null = defaultStorage(),
): void {
  if (message) storage?.setItem(SIGN_IN_ERROR_KEY, message);
  else storage?.removeItem(SIGN_IN_ERROR_KEY);
}

/**
 * Read once and forget. A refusal belongs to the attempt that caused it — one
 * left parked would greet the next visitor to the page with somebody else's
 * bad news, or the same person's after they fixed it.
 */
export function takeSignInError(
  storage: ErrorStorage | null = defaultStorage(),
): string | undefined {
  const raw = storage?.getItem(SIGN_IN_ERROR_KEY);
  if (raw === null || raw === undefined) return undefined;
  // Removed whether or not it survives the trim: a whitespace-only value is
  // the one input the trim exists for, and leaving it parked means the next
  // read finds it again forever.
  storage?.removeItem(SIGN_IN_ERROR_KEY);
  const message = raw.trim();
  return message ? message : undefined;
}

/**
 * The other thing the door may have to say: not that a sign-in failed, but
 * that a session ended under the tab — the server's verdict, met inside a
 * request, that took the tab here. The same storage and the same
 * read-once rule as the refusal above: a reload of the door is a new
 * visit, and greeting it with old news would be telling the next person
 * they were signed out of an account they never had.
 */
export const SESSION_ENDED_KEY = "selfeed.web.session_ended";

export function rememberSessionEnded(storage: ErrorStorage | null = defaultStorage()): void {
  storage?.setItem(SESSION_ENDED_KEY, "1");
}

export function takeSessionEnded(storage: ErrorStorage | null = defaultStorage()): boolean {
  const parked =
    storage?.getItem(SESSION_ENDED_KEY) !== null &&
    storage?.getItem(SESSION_ENDED_KEY) !== undefined;
  if (parked) storage?.removeItem(SESSION_ENDED_KEY);
  return parked;
}
